import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  initializeAuth,
  OAuthProvider,
  onAuthStateChanged,
  signOut,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { Plugins } from "@capacitor/core";
import config from "config.js";
import { isNativeIosPlatform } from "./deviceInfo";

const { GoogleAuth, SignInWithApple } = Plugins;

const localFirebaseConfig = {
  apiKey: "local-dev",
  authDomain: "mcsnet-myaccount-local.firebaseapp.com",
  projectId: "mcsnet-myaccount-local",
  appId: "local-dev",
};

const productionFirebaseConfig = {
  apiKey: "AIzaSyA6xRKMfBB_1RY1SyIUzQlUjk265XXBmMg",
  authDomain: "myaccount.mcsnet.ca",
  projectId: "myaccount-5a261",
  storageBucket: "myaccount-5a261.firebasestorage.app",
  messagingSenderId: "720071812511",
  appId: "1:720071812511:web:605508c6140cfc5aef49aa",
  measurementId: "G-VYFWZR7DT4",
};

const firebaseConfig = config.IS_LOCAL_ENV
  ? localFirebaseConfig
  : productionFirebaseConfig;

const APPLE_PRIVATE_RELAY_EMAIL_DOMAIN = "@privaterelay.appleid.com";
const MICROSOFT_CLIENT_ID = "37ed57c2-c5ca-4265-8bf2-d030b3dc0478";
const FIREBASE_TOKEN_TIMEOUT_MS = 15000;
const APPLE_PRIVATE_RELAY_ERROR_MESSAGE =
  "We respect your choice to keep your email private with Apple. For MyAccount access, we need the email address associated with your Apple ID so we can match it to the customer email already on file and verify that the account belongs to you. Please try Apple sign-in again and choose Share My Email, or use another sign-in option below.";

let nativeMicrosoftInitialized = false;

const callNativeMicrosoftPlugin = (callback) =>
  new Promise((resolve, reject) => callback(resolve, reject));

const initializeNativeMicrosoftAuth = async (plugin) => {
  if (nativeMicrosoftInitialized) {
    return;
  }

  await callNativeMicrosoftPlugin((resolve, reject) =>
    plugin.msalInit(resolve, reject, {
      clientId: MICROSOFT_CLIENT_ID,
      tenantId: "common",
      scopes: ["User.Read", "email"],
    })
  );
  nativeMicrosoftInitialized = true;
};

const createAppleNonce = () => {
  const values = new Uint8Array(32);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
};

const hashAppleNonce = async (rawNonce) => {
  const hash = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(rawNonce)
  );
  return Array.from(new Uint8Array(hash), (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
};

const app = initializeApp(firebaseConfig);
const isNativeIos = isNativeIosPlatform();
const auth = isNativeIos
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);

const signInWithNativeFirebaseCredential = async (credential) =>
  Promise.race([
    signInWithCredential(auth, credential),
    new Promise((resolve, reject) => {
      window.setTimeout(
        () =>
          reject(
            new Error("Firebase did not complete sign-in. Please try again.")
          ),
        15000
      );
    }),
  ]);

const getFirebaseIdToken = async (user) => {
  let timeoutId;

  try {
    return await Promise.race([
      user.getIdToken(),
      new Promise((resolve, reject) => {
        timeoutId = window.setTimeout(
          () =>
            reject(
              new Error("Firebase did not complete sign-in. Please try again.")
            ),
          FIREBASE_TOKEN_TIMEOUT_MS
        );
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

if (config.IS_LOCAL_ENV) {
  connectAuthEmulator(auth, "http://localhost:9099", {
    disableWarnings: true,
  });
}

export const signInWithGoogle = async () => {
  if (isNativeIosPlatform()) {
    const googleUser = await GoogleAuth.signIn();
    const idToken = googleUser?.authentication?.idToken;

    if (!idToken) {
      throw new Error("Google Sign-In did not return an ID token.");
    }

    const credential = GoogleAuthProvider.credential(idToken);
    const result = await signInWithNativeFirebaseCredential(credential);
    return getFirebaseIdToken(result.user);
  }

  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user.getIdToken();
};

export const signInWithMicrosoft = async () => {
  if (isNativeIosPlatform()) {
    const plugin = window.cordova?.plugins?.msalPlugin;
    if (!plugin) {
      throw new Error(
        "Native Microsoft Sign-In is not available. Please reinstall the app and try again."
      );
    }

    await initializeNativeMicrosoftAuth(plugin);
    const microsoftResult = await callNativeMicrosoftPlugin((resolve, reject) =>
      plugin.signInInteractive(resolve, reject)
    );

    if (!microsoftResult?.token && !microsoftResult?.idToken) {
      throw new Error(
        "Microsoft Sign-In did not return an authentication token."
      );
    }

    const provider = new OAuthProvider("microsoft.com");
    const credential = provider.credential({
      accessToken: microsoftResult.token,
      idToken: microsoftResult.idToken,
    });
    const firebaseResult = await signInWithNativeFirebaseCredential(credential);
    return getFirebaseIdToken(firebaseResult.user);
  }

  const provider = new OAuthProvider("microsoft.com");
  provider.addScope("email");
  const result = await signInWithPopup(auth, provider);
  return result.user.getIdToken();
};

export const signInWithApple = async () => {
  if (isNativeIosPlatform()) {
    const rawNonce = createAppleNonce();
    const result = await SignInWithApple.authorize({
      scopes: "email name",
      nonce: await hashAppleNonce(rawNonce),
    });
    const idToken = result?.response?.identityToken;

    if (!idToken) {
      throw new Error("Sign in with Apple did not return an identity token.");
    }

    const provider = new OAuthProvider("apple.com");
    const credential = provider.credential({ idToken, rawNonce });
    const firebaseResult = await signInWithNativeFirebaseCredential(credential);
    const email = firebaseResult.user.email || result.response.email || "";

    if (email.toLowerCase().endsWith(APPLE_PRIVATE_RELAY_EMAIL_DOMAIN)) {
      await signOut(auth);
      throw new Error(APPLE_PRIVATE_RELAY_ERROR_MESSAGE);
    }

    return getFirebaseIdToken(firebaseResult.user);
  }

  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");

  // Firebase opens popup auth in a separate Safari context for standalone iOS
  // PWAs, where the OAuth helper can lose its sessionStorage state.
  if (window.navigator.standalone === true) {
    return signInWithRedirect(auth, provider);
  }

  const result = await signInWithPopup(auth, provider);

  const email = result.user.email || "";
  if (email.toLowerCase().endsWith(APPLE_PRIVATE_RELAY_EMAIL_DOMAIN)) {
    await signOut(auth);
    throw new Error(APPLE_PRIVATE_RELAY_ERROR_MESSAGE);
  }

  return result.user.getIdToken();
};

export const getRememberedFirebaseToken = async () => {
  if (!isNativeIosPlatform()) {
    const redirectResult = await getRedirectResult(auth);
    const user =
      redirectResult?.user ||
      auth.currentUser ||
      (await new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          unsubscribe();
          resolve(currentUser);
        });
      }));

    if (!user) {
      return null;
    }

    const email = user.email || "";
    if (email.toLowerCase().endsWith(APPLE_PRIVATE_RELAY_EMAIL_DOMAIN)) {
      await signOut(auth);
      throw new Error(APPLE_PRIVATE_RELAY_ERROR_MESSAGE);
    }

    return user.getIdToken();
  }

  const redirectResult = null;
  const user =
    redirectResult?.user ||
    auth.currentUser ||
    (await new Promise((resolve) => {
      let unsubscribe = () => {};
      const finish = (currentUser) => {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve(currentUser);
      };
      const timeoutId = window.setTimeout(() => finish(null), 5000);
      unsubscribe = onAuthStateChanged(auth, finish);
    }));

  if (!user) {
    return null;
  }

  const email = user.email || "";
  if (email.toLowerCase().endsWith(APPLE_PRIVATE_RELAY_EMAIL_DOMAIN)) {
    await signOut(auth);
    throw new Error(APPLE_PRIVATE_RELAY_ERROR_MESSAGE);
  }

  return getFirebaseIdToken(user);
};

const signOutFromNativeProviders = async () => {
  if (!isNativeIosPlatform()) {
    return;
  }

  const nativeSignOuts = [];

  if (GoogleAuth && typeof GoogleAuth.signOut === "function") {
    nativeSignOuts.push(Promise.resolve().then(() => GoogleAuth.signOut()));
  }

  const microsoftPlugin = window.cordova?.plugins?.msalPlugin;
  if (microsoftPlugin && typeof microsoftPlugin.signOut === "function") {
    nativeSignOuts.push(
      (async () => {
        await initializeNativeMicrosoftAuth(microsoftPlugin);
        await callNativeMicrosoftPlugin((resolve, reject) =>
          microsoftPlugin.signOut(resolve, reject)
        );
      })()
    );
  }

  // A provider cache should never keep MyAccount itself signed in. Firebase
  // logout is still completed even if a provider is temporarily unavailable.
  await Promise.all(
    nativeSignOuts.map((nativeSignOut) => nativeSignOut.catch(() => null))
  );
};

export const signOutFromFirebase = async () => {
  if (!isNativeIosPlatform()) {
    return signOut(auth);
  }

  const firebaseSignOut = signOut(auth);
  await signOutFromNativeProviders();
  await firebaseSignOut;
};

export default auth;
